import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiFolder,
  FiUpload,
  FiFileText,
  FiLink,
  FiVideo,
  FiFile,
  FiClipboard,
  FiImage,
  FiMusic,
  FiCode,
  FiBook,
  FiMonitor,
  FiEdit2,
  FiTrash2,
  FiExternalLink,
  FiX,
  FiPlus,
  FiLock,
  FiAlertCircle,
} from "react-icons/fi";
import { workshopApi } from "../../services/workshopApi";
import { useUserContext } from "../../context/UserContext";
import LoadingSpinner from "../ui/LoadingSpinner";
import "./ResourceLibrary.css";

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.15 },
  },
};

// Icon mapping for resource types
const getResourceIcon = (type) => {
  const icons = {
    pdf: FiFileText,
    link: FiLink,
    video: FiVideo,
    document: FiFile,
    assignment: FiClipboard,
    slides: FiMonitor,
    presentation: FiMonitor,
    worksheet: FiClipboard,
    reading: FiBook,
    code: FiCode,
    image: FiImage,
    audio: FiMusic,
  };
  return icons[type] || FiFolder;
};

// Resource Card Component
const ResourceCard = ({ resource, canDelete, canEdit, onDelete, onEdit }) => {
  const IconComponent = getResourceIcon(resource.type);
  const [expanded, setExpanded] = useState(false);
  const descriptionLength = resource.description?.length || 0;
  const shouldTruncate = descriptionLength > 100;

  const handleDownload = () => {
    if (resource.url) {
      window.open(resource.url, "_blank");
    } else if (resource.fileKey) {
      window.open(`/api/download/${resource.fileKey}`, "_blank");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <motion.div
      className="wrl-card"
      variants={itemVariants}
      onClick={handleDownload}
    >
      <div className="wrl-card-header">
        <div className={`wrl-card-icon ${resource.type || "default"}`}>
          <IconComponent />
        </div>
        <div className="wrl-card-info">
          <h4 className="wrl-card-title">{resource.title}</h4>
          <div className="wrl-card-meta">
            <span className="wrl-badge wrl-badge-type">{resource.type}</span>
            {/* Show day label - use sessionDayLabel from backend, or generate from sessionDay */}
            {(() => {
              // Use backend-provided label, or generate from sessionDay number
              const dayLabel = resource.sessionDayLabel || 
                (resource.sessionDay === 0 ? 'General' : 
                 resource.sessionDay ? `Day ${resource.sessionDay}` : null);
              
              if (!dayLabel) return null;
              
              return (
                <span className={`wrl-badge wrl-badge-day ${dayLabel === 'General' ? 'wrl-badge-general' : ''}`}>
                  {dayLabel}
                </span>
              );
            })()}
            <span className="wrl-badge wrl-badge-date">
              {formatDate(resource.createdAt)}
            </span>
          </div>
        </div>
      </div>

      {resource.description && (
        <div className="wrl-card-description-wrapper">
          <p className="wrl-card-description">
            {expanded || !shouldTruncate
              ? resource.description
              : `${resource.description.substring(0, 100)}...`}
          </p>
          {shouldTruncate && (
            <button
              className="wrl-see-more-btn"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              {expanded ? "See less" : "See more"}
            </button>
          )}
        </div>
      )}

      {resource.uploader && (
        <div className="wrl-uploader">
          <img
            src={resource.uploader.profilePhotoUrl || "/default-avatar.png"}
            alt={resource.uploader.name}
            className="wrl-uploader-avatar"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <div className="wrl-uploader-fallback">
            {resource.uploader.name?.charAt(0).toUpperCase()}
          </div>
          <div className="wrl-uploader-info">
            <span className="wrl-uploader-name">
              Uploaded by {resource.uploader.name}
            </span>
            <span className="wrl-uploader-date">
              {formatDate(resource.uploadedAt || resource.createdAt)}
            </span>
          </div>
        </div>
      )}

      {resource.tags?.length > 0 && (
        <div className="wrl-tags">
          {resource.tags.map((tag, index) => (
            <span key={index} className="wrl-tag">
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="wrl-card-actions" onClick={(e) => e.stopPropagation()}>
        <button className="wrl-btn wrl-btn-primary" onClick={handleDownload}>
          <FiExternalLink />
          Open
        </button>
        {canEdit && (
          <button
            className="wrl-btn wrl-btn-secondary wrl-btn-icon-only"
            onClick={onEdit}
            title="Edit"
          >
            <FiEdit2 />
          </button>
        )}
        {canDelete && (
          <button
            className="wrl-btn wrl-btn-danger wrl-btn-icon-only"
            onClick={onDelete}
            title="Delete"
          >
            <FiTrash2 />
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Upload Form Component
const ResourceUploadForm = ({ onSubmit, onCancel, workshopDays = 3 }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "pdf",
    url: "",
    sessionDay: "",
    tags: [],
  });
  const [tagInput, setTagInput] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.url.trim()) {
      alert("Please fill in both title and URL");
      return;
    }
    onSubmit(formData);
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()],
      });
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  return (
    <motion.div
      className="wrl-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.div
        className="wrl-upload-modal"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="wrl-modal-header">
          <h3 className="wrl-modal-title">Upload Resource</h3>
          <button className="wrl-modal-close" onClick={onCancel}>
            <FiX />
          </button>
        </div>

        <div className="wrl-modal-body">
          <form onSubmit={handleSubmit}>
            <div className="wrl-form-group">
              <label className="wrl-form-label">
                Title <span className="required">*</span>
              </label>
              <input
                type="text"
                className="wrl-form-input"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Enter resource title..."
                maxLength={200}
                required
              />
            </div>

            <div className="wrl-form-group">
              <label className="wrl-form-label">Description</label>
              <textarea
                className="wrl-form-textarea"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Describe this resource..."
                rows={3}
                maxLength={1000}
              />
            </div>

            <div className="wrl-form-row">
              <div className="wrl-form-group">
                <label className="wrl-form-label">
                  Type <span className="required">*</span>
                </label>
                <select
                  className="wrl-form-select"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  required
                >
                  <option value="pdf">PDF Document</option>
                  <option value="link">External Link</option>
                  <option value="video">Video</option>
                  <option value="document">Document</option>
                  <option value="assignment">Assignment</option>
                  <option value="slides">Slides</option>
                  <option value="presentation">Presentation</option>
                  <option value="worksheet">Worksheet</option>
                  <option value="reading">Reading Material</option>
                  <option value="code">Code File</option>
                  <option value="image">Image</option>
                  <option value="audio">Audio</option>
                </select>
              </div>

              <div className="wrl-form-group">
                <label className="wrl-form-label">Session Day</label>
                <select
                  className="wrl-form-select"
                  value={formData.sessionDay}
                  onChange={(e) =>
                    setFormData({ ...formData, sessionDay: e.target.value })
                  }
                >
                  <option value="">General</option>
                  {/* Generate day options dynamically from workshopDays */}
                  {Array.from({ length: workshopDays }, (_, i) => (
                    <option key={i + 1} value={i + 1}>Day {i + 1}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="wrl-form-group">
              <label className="wrl-form-label">
                Resource URL <span className="required">*</span>
              </label>
              <input
                type="url"
                className="wrl-form-input"
                value={formData.url}
                onChange={(e) =>
                  setFormData({ ...formData, url: e.target.value })
                }
                placeholder="https://drive.google.com/file/d/..."
                required
              />
              <p className="wrl-form-hint">
                Provide a link to Google Drive, Dropbox, GitHub, or any
                accessible URL
              </p>
            </div>

            <div className="wrl-form-group">
              <label className="wrl-form-label">Tags</label>
              <div className="wrl-tag-input-wrapper">
                <input
                  type="text"
                  className="wrl-tag-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="Add a tag"
                />
                <button
                  type="button"
                  className="wrl-add-tag-btn"
                  onClick={addTag}
                >
                  <FiPlus />
                </button>
              </div>
              {formData.tags.length > 0 && (
                <div className="wrl-tags-list">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="wrl-tag-item">
                      {tag}
                      <button
                        type="button"
                        className="wrl-tag-remove"
                        onClick={() => removeTag(tag)}
                      >
                        <FiX />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="wrl-form-actions">
              <button type="submit" className="wrl-btn wrl-btn-primary">
                <FiUpload />
                Upload Resource
              </button>
              <button
                type="button"
                className="wrl-btn wrl-btn-secondary"
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Component
const ResourceLibrary = ({
  workshopId,
  userRole,
  workshop,
  onNavigateToOverview,
  canUpload: canUploadProp, // Permission prop from parent (for co-organizers)
}) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    type: "all",
    sessionDay: "",
    search: "",
  });
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  useUserContext(); // Auth context check

  // Debounce timer ref for search/filter changes
  const debounceTimerRef = useRef(null);

  // Get workshop days dynamically from workshop.duration.days
  const workshopDays = workshop?.duration?.days || 3;

  // Use prop if provided (for co-organizers), otherwise use API permissions
  // Organizers always have upload permission
  const canUpload = userRole === "organizer" 
    ? true 
    : canUploadProp !== undefined 
      ? canUploadProp 
      : (userPermissions?.canUpload || false);

  const canDeleteResource = (resource) => {
    return resource.userPermissions?.canDelete || false;
  };

  const canEditResource = (resource) => {
    return resource.userPermissions?.canEdit || false;
  };

  // Fetch resources function (called by debounced effect)
  const fetchResources = useCallback(async () => {
    try {
      setLoading(true);
      const response = await workshopApi.getWorkshopResources(
        workshopId,
        filter
      );
      if (response.success) {
        setResources(response.data.resources || []);
        setUserPermissions(response.data.userPermissions || null);
      }
    } catch (error) {
      setError(error.message);
} finally {
      setLoading(false);
    }
  }, [workshopId, filter]);

  // Debounced effect for filter changes - waits 300ms after user stops changing filters
  useEffect(() => {
    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set a new debounce timer (300ms delay)
    debounceTimerRef.current = setTimeout(() => {
      fetchResources();
    }, 300);

    // Cleanup function to clear timer on unmount or filter change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [fetchResources]);

  const handleUploadResource = async (resourceData) => {
    try {
      await workshopApi.uploadWorkshopResource(workshopId, resourceData);
      setShowUploadForm(false);
      fetchResources();
    } catch (error) {
alert("Failed to upload resource. Please try again.");
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (window.confirm("Are you sure you want to delete this resource?")) {
      try {
        await workshopApi.deleteWorkshopResource(workshopId, resourceId);
        fetchResources();
      } catch (error) {
alert("Failed to delete resource. Please try again.");
      }
    }
  };

  const handleEditResource = async (resourceId) => {
alert("Edit functionality coming soon!");
  };

  // Loading State
  if (loading) {
    return (
      <div className="wrl-loading-skeleton">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="wrl-skeleton-card">
            <div className="wrl-skeleton-icon" />
            <div className="wrl-skeleton-content">
              <div className="wrl-skeleton-line wrl-skeleton-title" />
              <div className="wrl-skeleton-line wrl-skeleton-desc" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error State - Access Denied
  if (error) {
    const isAccessDenied =
      error.includes("403") ||
      error.includes("401") ||
      error.includes("503") ||
      error.includes("unauthorized") ||
      error.includes("not a participant") ||
      error.includes("API call failed with status 403");

    if (isAccessDenied || userRole === "non-member" || userRole === "visitor") {
      return (
        <motion.div
          className="wrl-access-denied"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="wrl-access-denied-icon">
            <FiLock />
          </div>
          <h3 className="wrl-access-denied-title">
            Resource Access Restricted
          </h3>
          <p className="wrl-access-denied-text">
            You need to be a workshop participant to view and access workshop
            resources.
          </p>
          <button
            className="wrl-btn wrl-btn-primary"
            onClick={onNavigateToOverview}
          >
            View Workshop Details
          </button>
        </motion.div>
      );
    }

    return (
      <motion.div
        className="wrl-error"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="wrl-error-icon">
          <FiAlertCircle />
        </div>
        <p className="wrl-error-text">Failed to load resources: {error}</p>
        <button className="wrl-retry-btn" onClick={fetchResources}>
          Try Again
        </button>
      </motion.div>
    );
  }

  return (
    <div className="wrl-container">
      {/* Header */}
      <motion.div
        className="wrl-header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="wrl-header-left">
          <div className="wrl-header-icon">
            <FiFolder />
          </div>
          <div>
            <h2 className="wrl-header-title">Workshop Resources</h2>
            <span className="wrl-header-count">
              {resources.length} resources
            </span>
          </div>
        </div>
        {canUpload && (
          <button
            className="wrl-upload-btn"
            onClick={() => setShowUploadForm(true)}
          >
            <FiUpload />
            Upload Resource
          </button>
        )}
      </motion.div>

      {/* Filters */}
      <motion.div
        className="wrl-filters"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="wrl-filter-group">
          <select
            className="wrl-filter-select"
            value={filter.type}
            onChange={(e) => setFilter({ ...filter, type: e.target.value })}
          >
            <option value="all">All Types</option>
            <option value="pdf">PDFs</option>
            <option value="link">Links</option>
            <option value="video">Videos</option>
            <option value="document">Docs</option>
            <option value="assignment">Tasks</option>
            <option value="slides">Slides</option>
            <option value="presentation">PPTs</option>
            <option value="worksheet">Sheets</option>
            <option value="reading">Reading</option>
            <option value="code">Code</option>
            <option value="image">Images</option>
            <option value="audio">Audio</option>
          </select>

          <select
            className="wrl-filter-select"
            value={filter.sessionDay}
            onChange={(e) =>
              setFilter({ ...filter, sessionDay: e.target.value })
            }
          >
            <option value="">All Days</option>
            {/* Generate day options dynamically from workshop.duration.days */}
            {Array.from({ length: workshopDays }, (_, i) => (
              <option key={i + 1} value={i + 1}>Day {i + 1}</option>
            ))}
          </select>
        </div>

        <div className="wrl-search-wrapper">
          <input
            type="text"
            className="wrl-search-input"
            placeholder="Search resources..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
          />
        </div>
      </motion.div>

      {/* Resources Grid */}
      <motion.div
        className="wrl-grid"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {resources.length === 0 ? (
          <motion.div className="wrl-empty" variants={itemVariants}>
            <div className="wrl-empty-icon">
              <FiFolder />
            </div>
            <h3 className="wrl-empty-title">No resources yet</h3>
            <p className="wrl-empty-text">
              {canUpload
                ? "Upload your first resource to get started!"
                : userPermissions?.participantStatus === "pending"
                ? "You'll be able to upload resources once your participation is approved."
                : userPermissions?.participantStatus === "payment_pending"
                ? "You'll be able to upload resources once your payment is approved."
                : "Resources will appear here when uploaded by workshop organizers and participants."}
            </p>
          </motion.div>
        ) : (
          resources.map((resource) => (
            <ResourceCard
              key={resource._id}
              resource={resource}
              canDelete={canDeleteResource(resource)}
              canEdit={canEditResource(resource)}
              onDelete={() => handleDeleteResource(resource._id)}
              onEdit={() => handleEditResource(resource._id)}
            />
          ))
        )}
      </motion.div>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadForm && (
          <ResourceUploadForm
            onSubmit={handleUploadResource}
            onCancel={() => setShowUploadForm(false)}
            workshopDays={workshopDays}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResourceLibrary;
